import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react'

import { format, parse } from 'date-fns'
import { enUS, ja } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { ActiveDotProps } from 'recharts/types/util/types'

import { Box, Flex, Text, useToken } from '@enechain/ecloud-designsystem'

import { MarginReportLegend } from './MarginReportLegend'
import { MarginReportPeriodSelector } from './MarginReportPeriodSelector'
import { MarginReportTooltip } from './MarginReportTooltip'
import { deriveMarginReportEntry } from './derive'
import { formatYenOku } from './format'
import {
  MarginReportSeriesDotShape,
  MarginReportSeriesKey,
  MarginReportSeriesStyle,
  assertUnreachableDotShape,
  createMarginReportVisibility,
  isSeriesAvailable,
  marginReportSeries,
  marginReportSeriesByKey,
  resolveMarginReportSeriesStyle,
} from './series'
import { MarginReportChartInput } from './types'

// series.ts に定義が無い maxTradableAmount（最大取引可能金額）は derive.ts で算出した値を
// チャートデータ組み立て時に行ごとに合成する。Tooltip は payload を MarginReportChartInput として
// 読むだけなので、この追加フィールドがあっても無害。
type MarginReportChartRow = MarginReportChartInput & {
  maxTradableAmount: number | null
}

// marginCallTiming は ReferenceLine（縦線マーカー）専用で、折れ線/エリアとして描画する
// dataKey を持たないため、系列描画のループからは除外する。
type ChartSeriesKey = Exclude<MarginReportSeriesKey, 'marginCallTiming'>

// MarginReportChartRow 型ガード
function isMarginReportChartRow(value: unknown): value is MarginReportChartRow {
  return (
    typeof value === 'object' &&
    value !== null &&
    'date' in value &&
    'deposits' in value &&
    'unrealizedAmount' in value &&
    'realizedAmount' in value &&
    'requiredMargin' in value &&
    'initialMargin' in value &&
    'surplus' in value &&
    'maxTradableAmount' in value
  )
}

// recharts が dot レンダラーに渡す props から payload を安全に取り出す
function extractMarginReportChartRow(
  props: unknown,
): MarginReportChartRow | null {
  if (typeof props !== 'object' || props === null) {
    return null
  }
  if (!('payload' in props)) {
    return null
  }
  return isMarginReportChartRow(props.payload) ? props.payload : null
}

const BREAKPOINT = 896
const DOT_SIZE_DEFAULT = 5
const DOT_SIZE_SMALL = 4

// ドットコンポーネントのprops型定義。null/isFinite の妥当性チェックは呼び出し側
// （renderChartSeriesDot）で一元的に行うため、形状コンポーネント自身は座標・色・
// サイズだけを受け取って描画するだけの薄い部品にする。
type DotProps = {
  cx: number
  cy: number
  fill: string
  size?: number
}

// 三角形ドット
const TriangleDot = (props: DotProps): React.ReactNode => {
  const { cx, cy, fill, size = DOT_SIZE_DEFAULT } = props
  return (
    <polygon
      points={`${cx},${cy - size} ${cx - size},${cy + size} ${cx + size},${cy + size}`}
      fill={fill}
      stroke={fill}
      strokeWidth="1"
    />
  )
}

// 四角形ドット
const SquareDot = (props: DotProps): React.ReactNode => {
  const { cx, cy, fill, size = DOT_SIZE_DEFAULT } = props
  const points = [
    `${cx - size},${cy - size}`,
    `${cx + size},${cy - size}`,
    `${cx + size},${cy + size}`,
    `${cx - size},${cy + size}`,
  ].join(' ')
  return <polygon points={points} fill={fill} stroke={fill} strokeWidth="1" />
}

// ダイヤモンド形ドット
const DiamondDot = (props: DotProps): React.ReactNode => {
  const { cx, cy, fill, size = DOT_SIZE_DEFAULT } = props
  const points = [
    `${cx},${cy - size}`,
    `${cx + size},${cy}`,
    `${cx},${cy + size}`,
    `${cx - size},${cy}`,
  ].join(' ')
  return <polygon points={points} fill={fill} stroke={fill} strokeWidth="1" />
}

// 六角形ドット
const HexagonDot = (props: DotProps): React.ReactNode => {
  const { cx, cy, fill, size = DOT_SIZE_DEFAULT } = props
  const points = Array.from({ length: 6 }, (_, i) => {
    const angle = -Math.PI / 2 + (Math.PI / 3) * i
    return `${cx + size * Math.cos(angle)},${cy + size * Math.sin(angle)}`
  }).join(' ')
  return <polygon points={points} fill={fill} stroke={fill} strokeWidth="1" />
}

// 円形ドット
const CircleDot = (props: DotProps): React.ReactNode => {
  const { cx, cy, fill, size = DOT_SIZE_DEFAULT } = props
  return (
    <circle
      cx={cx}
      cy={cy}
      r={size}
      fill={fill}
      stroke={fill}
      strokeWidth="1"
    />
  )
}

// series.ts の dot 形状指定から、チャート本体用のドットレンダラー（recharts の dot prop）を作る。
// 凡例側（MarginReportLegend）の固定サイズスウォッチとは別に、こちらは実測値（value）が
// null の点や、非表示レイアウト時の縮小サイズ（dotSize）を扱う。
// 'none' の場合は dot={false} を渡す（M-6）ため、この関数自体は呼ばれない。
function renderChartSeriesDot(
  shape: Exclude<MarginReportSeriesDotShape, 'none'>,
  color: string,
  size: number,
  key: ChartSeriesKey,
) {
  // eslint の react/display-name 対策で名前付き関数式にする（無名関数を返すと
  // 「コンポーネントらしき関数に displayName が無い」と誤検知されるため）。
  return function seriesDot(props: ActiveDotProps): React.JSX.Element {
    const datum = extractMarginReportChartRow(props)
    const value = datum == null ? null : datum[key]
    // null/isFinite のチェックはここ一箇所のみ。通過後の座標・値は形状コンポーネントに
    // そのまま渡す（M-7）。
    if (value == null || !isFinite(props.cx) || !isFinite(props.cy)) {
      return <g />
    }
    const { cx, cy } = props
    switch (shape) {
      case 'circle':
        return <CircleDot cx={cx} cy={cy} fill={color} size={size} />
      case 'square':
        return <SquareDot cx={cx} cy={cy} fill={color} size={size} />
      case 'diamond':
        return <DiamondDot cx={cx} cy={cy} fill={color} size={size} />
      case 'hexagon':
        return <HexagonDot cx={cx} cy={cy} fill={color} size={size} />
      case 'triangle':
        return <TriangleDot cx={cx} cy={cy} fill={color} size={size} />
      default:
        return assertUnreachableDotShape(shape)
    }
  }
}

// series.ts の1系列分の定義（スタイル解決済み）から、実際の recharts 要素（Line/Area）を組み立てる。
// fill.kind === 'none' は Line、それ以外（'solid' | 'gradient'）は Area として描画する。
// グラデーション塗りの <linearGradient> 自体は Chart 側の <defs>（id: `${key}Gradient`）に置く。
function renderComposedSeries(
  key: ChartSeriesKey,
  style: MarginReportSeriesStyle,
  name: string,
  hidden: boolean,
  dotSize: number,
): React.ReactElement {
  const shared = {
    yAxisId: 'left' as const,
    type: 'linear' as const,
    dataKey: key,
    name,
    stroke: style.stroke,
    strokeWidth: style.strokeWidth,
    strokeDasharray: style.strokeDasharray,
    hide: hidden,
    connectNulls: true,
    isAnimationActive: false,
    // dot: 'none' の系列は dot={false} を渡し、recharts に毎点空の <g/> を
    // 描画させない（M-6）。
    dot:
      style.dot === 'none'
        ? false
        : renderChartSeriesDot(style.dot, style.stroke, dotSize, key),
  }

  if (style.fill.kind === 'none') {
    return <Line key={key} {...shared} />
  }
  if (style.fill.kind === 'solid') {
    return (
      <Area
        key={key}
        {...shared}
        fill={style.fill.color}
        fillOpacity={style.fill.opacity}
      />
    )
  }
  return <Area key={key} {...shared} fill={`url(#${key}Gradient)`} />
}

// 「現在の必要証拠金」バッジの背景色。
const COVERAGE_BADGE_BACKGROUND: Record<'shortfall' | 'surplus', string> = {
  // rgb(255, 235, 238) はプロトタイプ DOM 実測値（2026-09-15）。DS の Surface.negativeLow(#fff5f5) /
  // Surface.negativeMid(#ffdedf) のいずれとも一致しないためリテラルで指定する。
  shortfall: 'rgb(255, 235, 238)',
  // rgb(235, 255, 239) は DS の Surface.positiveLow（#ebffef）と厳密に一致するためトークンを使う。
  surplus: 'Surface.positiveLow',
}

// rgb(9, 9, 11) はプロトタイプ DOM 実測値（2026-09-15）。DS のテキストカラートークンに
// 一致するものが無いためリテラルで指定する（余剰・不足とも同色）。
const COVERAGE_BADGE_TEXT_COLOR = 'rgb(9, 9, 11)'

const MarginReportCoverageBadge: React.FC<{
  variant: 'shortfall' | 'surplus'
  label: string
  amount: number | null
  okuUnit: string
  okuDivisor: number
  fractionDigits: number
}> = ({ variant, label, amount, okuUnit, okuDivisor, fractionDigits }) => (
  <Flex
    alignItems="center"
    paddingX="2"
    paddingY="0.5"
    borderRadius="radiiBase"
    backgroundColor={COVERAGE_BADGE_BACKGROUND[variant]}
  >
    <Text
      fontSize="14px"
      fontWeight="600"
      color={COVERAGE_BADGE_TEXT_COLOR}
      whiteSpace="nowrap"
    >
      {label}{' '}
      {amount !== null
        ? `¥${formatYenOku(amount, okuUnit, okuDivisor, fractionDigits)}`
        : '—'}
    </Text>
  </Flex>
)

type Props = {
  data: MarginReportChartInput[]
  marginCallDates: Set<string>
  period: 30 | 90
  onPeriodChange: (period: 30 | 90) => void
  // ハイブリッドスキーム（履行保証あり）かどうか。系列の表示可否・スタイル・
  // カバー額の判定に使う唯一のフラグ（Table と同じ流儀）。
  isHybrid: boolean
}

export const MarginReportChart: React.FC<Props> = ({
  data,
  marginCallDates,
  period,
  onPeriodChange,
  isHybrid,
}) => {
  const { t, i18n } = useTranslation(['component'])
  // recharts は chakra の token 文字列を解決しないため、SVG に渡す色は
  // useToken で実値 (semantic token は CSS 変数参照) に解決する
  const [gridLineColor, negativeAreaColor, emphasisLineColor, axisLineColor] =
    useToken('colors', [
      'gray.200',
      'Surface.positiveMid',
      'Border.highEmphasis',
      'Border.lowEmphasis',
    ])
  const okuUnit = t('component:features.margin_report.oku_unit')
  const okuDivisor = i18n.language === 'ja' ? 1e8 : 1e6
  const fractionDigits = i18n.language === 'ja' ? 1 : 0
  const dateLocale = i18n.language === 'ja' ? ja : enUS
  const xAxisDateFormat = i18n.language === 'ja' ? 'M/d（E）' : 'M/d (EEE)'

  const [isSmallLayout, setIsSmallLayout] = useState<boolean>(
    () => window.innerWidth < BREAKPOINT,
  )
  useLayoutEffect(() => {
    const update = (): void => {
      setIsSmallLayout(window.innerWidth < BREAKPOINT)
    }
    window.addEventListener('resize', update)
    return (): void => window.removeEventListener('resize', update)
  }, [])
  const dotSize = isSmallLayout ? DOT_SIZE_SMALL : DOT_SIZE_DEFAULT

  // 凡例選択（表示 ON/OFF）。isHybrid はクエリ完了後に false → true へ遷移しうるため、
  // 変化のたびに初期値を作り直す（series.ts の createMarginReportVisibility の doc コメント参照）。
  // ユーザーが手で切り替えた後に isHybrid が変わった場合は初期値にリセットする仕様。
  //
  // React 公式の "Adjusting state when a prop changes" パターン（render 中に prevIsHybrid
  // との差分を見て setState する）を使う。useEffect でこれをやると、isHybrid が変わった
  // レンダーで一瞬だけ古い visibility のまま描画され（コミット後に effect が走って
  // 再レンダーされるまでのちらつき）、かつマウント時にも無駄な 2 回目のレンダーが発生する。
  const [prevIsHybrid, setPrevIsHybrid] = useState(isHybrid)
  const [visibility, setVisibility] = useState<
    Record<MarginReportSeriesKey, boolean>
  >(() => createMarginReportVisibility(isHybrid))
  if (prevIsHybrid !== isHybrid) {
    setPrevIsHybrid(isHybrid)
    setVisibility(createMarginReportVisibility(isHybrid))
  }

  const handleToggleVisibility = useCallback(
    (key: MarginReportSeriesKey, value: boolean): void => {
      setVisibility((prev) => ({ ...prev, [key]: value }))
    },
    [],
  )

  // maxTradableAmount（最大取引可能金額）は MarginReportChartInput に無い派生値のため、
  // 行ごとに derive.ts で算出してチャートデータに合成する（ロジックは derive.ts のみに置く）。
  const chartData = useMemo<MarginReportChartRow[]>(
    () =>
      data.map((entry) => ({
        ...entry,
        maxTradableAmount: deriveMarginReportEntry(entry, isHybrid)
          .maxTradableAmount,
      })),
    [data, isHybrid],
  )

  // 証拠金コール日を縦線マーカーとして使用
  const markers = useMemo(
    () => Array.from(marginCallDates).map((date) => ({ date, label: '' })),
    [marginCallDates],
  )

  // X軸カスタムティック：マージンコール日はオレンジで強調
  const renderXAxisTick = useCallback(
    (props: unknown): React.ReactElement<SVGElement> => {
      if (typeof props !== 'object' || props === null) {
        return <g />
      }
      const p = props as Record<string, unknown>
      if (
        typeof p['x'] !== 'number' ||
        typeof p['y'] !== 'number' ||
        typeof p['payload'] !== 'object' ||
        p['payload'] === null
      ) {
        return <g />
      }
      const payload = p['payload'] as Record<string, unknown>
      if (typeof payload['value'] !== 'string') {
        return <g />
      }
      const { x, y } = p
      const value = payload['value']
      const isMarginCall = marginCallDates.has(value)
      return (
        <text
          x={x as number}
          y={y as number}
          dy={4}
          transform={`rotate(-45, ${x as number}, ${y as number})`}
          textAnchor="end"
          fill={isMarginCall ? '#EA740C' : '#666'}
          // chakra v3 の preflight (`* { font: inherit }`) が SVG の
          // font-size/font-weight プレゼンテーション属性を上書きするため、
          // 属性ではなく inline style で指定する
          style={{ fontSize: 10, fontWeight: isMarginCall ? 700 : 400 }}
        >
          {format(parse(value, 'yyyy-MM-dd', new Date()), xAxisDateFormat, {
            locale: dateLocale,
          })}
        </text>
      )
    },
    [dateLocale, marginCallDates, xAxisDateFormat],
  )

  // 最新の必要証拠金（null でない最後のエントリを使用）
  const latestRequiredMargin =
    data.findLast((d) => d.requiredMargin !== null)?.requiredMargin ?? null

  // 最新の数値が入っている日付の不足・余剰金（返金可能）。カバー額の定義（預託金 or
  // 預託金＋履行保証）は derive.ts に一元化されており、ここでは再計算しない。「揃っている」の
  // 判定自体も deriveMarginReportEntry(e, isHybrid).shortage !== null（必要証拠金とカバー額の
  // 両方が揃って初めて不足額が計算できる日）を通して derive 経由で行い、Chart 側で
  // deposits/requiredMargin の null 判定を再実装しない。
  const latestEntry = data.findLast(
    (d) => deriveMarginReportEntry(d, isHybrid).shortage !== null,
  )
  const latestDerived =
    latestEntry != null ? deriveMarginReportEntry(latestEntry, isHybrid) : null
  const isShortfall =
    latestDerived != null &&
    latestDerived.shortage !== null &&
    latestDerived.shortage > 0

  // チャートのアクセシビリティ用の説明文を生成
  const accessibilityDescription = useMemo(() => {
    const totalData = data.reduce(
      (acc, point) => {
        acc.deposits += point.deposits ?? 0
        acc.unrealizedAmount += point.unrealizedAmount ?? 0
        acc.realizedAmount += point.realizedAmount ?? 0
        acc.requiredMargin += point.requiredMargin ?? 0
        return acc
      },
      {
        deposits: 0,
        unrealizedAmount: 0,
        realizedAmount: 0,
        requiredMargin: 0,
      },
    )

    return t(
      'component:features.margin_report.chart.accessibility_description',
      {
        count: data.length,
        deposits: formatYenOku(
          totalData.deposits,
          okuUnit,
          okuDivisor,
          fractionDigits,
        ),
        unrealizedAmount: formatYenOku(
          totalData.unrealizedAmount,
          okuUnit,
          okuDivisor,
          fractionDigits,
        ),
        realizedAmount: formatYenOku(
          totalData.realizedAmount,
          okuUnit,
          okuDivisor,
          fractionDigits,
        ),
        requiredMargin: formatYenOku(
          totalData.requiredMargin,
          okuUnit,
          okuDivisor,
          fractionDigits,
        ),
      },
    )
  }, [data, fractionDigits, okuDivisor, okuUnit, t])

  // 描画対象の系列（marginCallTiming を除く）。isHybrid で表示対象になりうる系列を絞り込んだ上で、
  // drawOrder（series.ts）昇順に並べ替える。この並びが描画の重なり順（後に描画したものが手前）
  // になる。凡例の表示順（marginReportSeries の並び）とは独立している。
  const drawableSeries = useMemo(
    () =>
      marginReportSeries
        .filter(
          (
            def,
          ): def is (typeof marginReportSeries)[number] & {
            key: ChartSeriesKey
          } =>
            def.key !== 'marginCallTiming' && isSeriesAvailable(def, isHybrid),
        )
        .sort((a, b) => a.drawOrder - b.drawOrder),
    [isHybrid],
  )

  // 左右 Y 軸を同一スケールにするためのドメイン・tick 計算。表示中の drawableSeries から
  // 値を集める（I-3）ことで、系列を追加した際に Y 軸レンジ計算だけ更新し忘れる二重管理を防ぐ。
  const { yDomain, yTicks } = useMemo(() => {
    if (chartData.length === 0) {
      return { yDomain: [0, 1] as [number, number], yTicks: [0, 1] }
    }
    const allValues = chartData
      .flatMap((d) =>
        drawableSeries.map((def) => (visibility[def.key] ? d[def.key] : null)),
      )
      .filter((v): v is number => v !== null)
    if (allValues.length === 0) {
      return { yDomain: [0, 1] as [number, number], yTicks: [0, 1] }
    }
    const max = Math.max(...allValues)
    const min = Math.min(...allValues)
    const domainMax = Math.max(0, max)
    const domainMin = Math.min(0, min)

    // domainMax === domainMin（全データ同値など）の場合に備えてドメインにパディングを入れる
    let paddedMax = domainMax
    let paddedMin = domainMin
    if (domainMax === domainMin) {
      const base = domainMax === 0 ? 1 : Math.abs(domainMax)
      const padding = base * 0.1 !== 0 ? base * 0.1 : 1
      paddedMax = domainMax + padding
      paddedMin = domainMin - padding
    }
    let range = paddedMax - paddedMin
    if (range <= 0) {
      range = 1
      paddedMax = paddedMin + range
    }

    // range/10 をキリの良い値（1・2・5 × 10^n）に丸めた step を算出
    const rawStep = range / 10
    const mag = Math.pow(10, Math.floor(Math.log10(rawStep)))
    const norm = rawStep / mag
    const step =
      norm <= 1 ? mag : norm <= 2 ? 2 * mag : norm <= 5 ? 5 * mag : 10 * mag

    // min/max を step の倍数に揃えて 0 を必ず含む
    const tickMin = Math.floor(paddedMin / step) * step
    const tickMax = Math.ceil(paddedMax / step) * step
    const ticks = Array.from(
      { length: Math.round((tickMax - tickMin) / step) + 1 },
      (_, i) => Math.round((tickMin + i * step) * 1e10) / 1e10,
    )
    return {
      yDomain: [tickMin, tickMax] as [number, number],
      yTicks: ticks,
    }
  }, [chartData, visibility, drawableSeries])

  const formatYAxisTick = useCallback(
    (v: number): string => {
      if (fractionDigits === 0) {
        return `${Math.round(v / okuDivisor)}${okuUnit}`
      }
      const oku = Math.round((v / okuDivisor) * 10) / 10
      return `${oku % 1 === 0 ? oku.toFixed(0) : oku.toFixed(1)}${okuUnit}`
    },
    [okuUnit, okuDivisor, fractionDigits],
  )

  const yAxisWidth = useMemo(() => {
    if (yTicks.length === 0) {
      return 42
    }
    const maxAbsTick = Math.max(...yTicks.map(Math.abs))
    // 実際に軸へ描画される tick ラベルと同じロジック（formatYAxisTick）で幅を測る。
    // ここで独自に丸めロジックを再実装しない（I-5）。
    const longestLabel = formatYAxisTick(maxAbsTick)
    const extraWidth = yTicks.some((t) => t < 0 && Math.abs(t) === maxAbsTick)
      ? 8
      : 0
    // 8px per character + 8px padding, minimum 42
    return Math.max(42, longestLabel.length * 8 + 8 + extraWidth)
  }, [yTicks, formatYAxisTick])

  return (
    <Box
      padding="4"
      borderRadius="md"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="Border.midEmphasis"
    >
      {/* ヘッダーエリア */}
      <Flex
        justifyContent="space-between"
        alignItems="center"
        paddingBottom="3"
        marginBottom="4"
        borderBottomWidth="1px"
        borderBottomStyle="solid"
        borderBottomColor="gray.200"
      >
        <Text fontSize="20px" fontWeight="700" color="gray.900">
          {t(
            isHybrid
              ? 'component:features.margin_report.chart.title_hybrid'
              : 'component:features.margin_report.chart.title',
          )}
        </Text>

        <Flex alignItems="center" gap="3">
          {/* 現在の必要証拠金 */}
          <Flex
            backgroundColor="white"
            borderColor="gray.300"
            borderWidth="1px"
            borderStyle="solid"
            borderRadius="radiiLg"
            paddingY="2"
            paddingX="4"
            height="40px"
            gap="2"
            alignItems="center"
          >
            <Text fontSize="14px" fontWeight="600" color="gray.900">
              {t(
                'component:features.margin_report.chart.current_required_margin',
              )}
            </Text>
            <Flex alignItems="center" gap="1">
              <Text fontSize="14px" fontWeight="600" color="red.600">
                {latestRequiredMargin !== null
                  ? `¥${formatYenOku(latestRequiredMargin, okuUnit, okuDivisor, fractionDigits)}`
                  : '—'}
              </Text>
              {latestRequiredMargin !== null && (
                <Text fontSize="12px" fontWeight="300" color="gray.600">
                  （¥ {Math.round(latestRequiredMargin).toLocaleString('ja-JP')}
                  ）
                </Text>
              )}
            </Flex>
            {latestDerived != null && (
              <MarginReportCoverageBadge
                variant={isShortfall ? 'shortfall' : 'surplus'}
                label={t(
                  isShortfall
                    ? 'component:features.margin_report.chart.shortfall'
                    : 'component:features.margin_report.chart.surplus',
                )}
                amount={
                  isShortfall
                    ? latestDerived.shortage
                    : latestDerived.refundableSurplus
                }
                okuUnit={okuUnit}
                okuDivisor={okuDivisor}
                fractionDigits={fractionDigits}
              />
            )}
          </Flex>
          <MarginReportPeriodSelector
            period={period}
            onChange={onPeriodChange}
          />
        </Flex>
      </Flex>

      {/* 凡例（チェックボックス／スイッチ） */}
      {/* DS の Checkbox は checked prop のマウント後の変更を表示に反映しないため、
          visibility をプログラム的に作り直す isHybrid の切替時は凡例ごとリマウントして
          チェック表示と state を同期させる（deposits はモード間で初期 ON/OFF が異なる）。 */}
      <MarginReportLegend
        key={isHybrid ? 'hybrid' : 'deposit-only'}
        isHybrid={isHybrid}
        visibility={visibility}
        onToggleVisibility={handleToggleVisibility}
      />

      <Box width="100%" overflowX="auto">
        <Box
          minWidth={period === 90 ? '2000px' : '1200px'}
          height="360px"
          backgroundColor="white"
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{ top: 8, right: 18, left: 6, bottom: 24 }}
              accessibilityLayer
              desc={accessibilityDescription}
            >
              <defs>
                {/* グラデーション塗りの <linearGradient> は series.ts の stroke 色から生成する
                    （I-4）。id は series.ts の doc コメントにある `${key}Gradient` 命名規約に従う。
                    offset/stopOpacity の数値自体は現行の見た目を維持する。 */}
                {drawableSeries
                  .filter(
                    (def) =>
                      resolveMarginReportSeriesStyle(def, isHybrid).fill
                        .kind === 'gradient',
                  )
                  .map((def) => {
                    const { stroke } = resolveMarginReportSeriesStyle(
                      def,
                      isHybrid,
                    )
                    return (
                      <linearGradient
                        key={def.key}
                        id={`${def.key}Gradient`}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor={stroke}
                          stopOpacity={0.4}
                        />
                        <stop offset="70%" stopColor={stroke} stopOpacity={0} />
                      </linearGradient>
                    )
                  })}
              </defs>
              <CartesianGrid
                stroke={gridLineColor}
                strokeWidth={1}
                horizontal={false}
              />
              {/* 各tick位置に薄い横線 */}
              {yTicks
                .filter((tick) => tick !== 0)
                .map((tick) => (
                  <ReferenceLine
                    key={`hgrid-${tick}`}
                    yAxisId="left"
                    y={tick}
                    stroke={gridLineColor}
                    strokeWidth={1}
                  />
                ))}
              {/* マイナス領域の背景色 */}
              {yDomain[0] < 0 && (
                <ReferenceArea
                  yAxisId="left"
                  y1={yDomain[0]}
                  y2={0}
                  fill={negativeAreaColor}
                  stroke="none"
                />
              )}
              {/* 0ラインは点線 */}
              <ReferenceLine
                yAxisId="left"
                y={0}
                stroke={emphasisLineColor}
                strokeWidth={2}
                strokeDasharray="3 3"
              />

              <XAxis
                dataKey="date"
                tick={renderXAxisTick}
                interval={0}
                axisLine={{
                  stroke: axisLineColor,
                  strokeWidth: 1,
                }}
                tickLine={{
                  stroke: axisLineColor,
                  strokeWidth: 1,
                }}
                name={t('component:features.margin_report.chart.x_axis_name')}
              />

              <YAxis
                yAxisId="left"
                orientation="left"
                tickFormatter={formatYAxisTick}
                width={yAxisWidth}
                domain={yDomain}
                ticks={yTicks}
                interval={0}
                axisLine={{
                  stroke: axisLineColor,
                  strokeWidth: 1,
                }}
                tickLine={{
                  stroke: axisLineColor,
                  strokeWidth: 1,
                }}
                name={t('component:features.margin_report.chart.y_axis_name')}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tickFormatter={formatYAxisTick}
                width={yAxisWidth}
                domain={yDomain}
                ticks={yTicks}
                interval={0}
                allowDataOverflow
                axisLine={{
                  stroke: axisLineColor,
                  strokeWidth: 1,
                }}
                tickLine={{
                  stroke: axisLineColor,
                  strokeWidth: 1,
                }}
                name={t('component:features.margin_report.chart.y_axis_name')}
              />

              <Tooltip
                // 全系列が OFF（hide）でも payload を空にしないために必要。
                includeHidden
                cursor={{
                  stroke: emphasisLineColor,
                  strokeWidth: 1,
                }}
                content={<MarginReportTooltip isHybrid={isHybrid} />}
              />

              {/* 縦のマーカー（証拠金コール日） */}
              {visibility.marginCallTiming &&
                markers.map((m) => (
                  <ReferenceLine
                    key={m.date}
                    yAxisId="left"
                    x={m.date}
                    stroke={
                      marginReportSeriesByKey.marginCallTiming.style.stroke
                    }
                    strokeWidth={
                      marginReportSeriesByKey.marginCallTiming.style.strokeWidth
                    }
                  />
                ))}

              {/* 各系列を独立したエリア／折れ線として表示（積み上げなし）。
                  色・線種・塗り・ドット形状は series.ts が唯一の情報源。 */}
              {drawableSeries.map((def) =>
                renderComposedSeries(
                  def.key,
                  resolveMarginReportSeriesStyle(def, isHybrid),
                  t(def.labelKey),
                  !visibility[def.key],
                  dotSize,
                ),
              )}

              {/* 右軸を描画させるためのダミー系列（dataKeyは存在しないキーにして右軸ドメインに干渉させない） */}
              <Line
                yAxisId="right"
                dataKey="_dummy"
                stroke="transparent"
                dot={false}
                isAnimationActive={false}
                aria-hidden
                legendType="none"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Box>
  )
}
